import { EXPERTS, GRID_COLS, GRID_ROWS, LAND } from "./globe-grid";

/** Sparsest to densest; `shadeGlobe` hands its painter an index into this. */
export const GLYPHS = "░▒▓█";

/** Latitude at the centre of the disc, in degrees. */
const TILT = 18;

/** Direction the light comes from, in view space (x right, y up, z out). */
const LIGHT = normalize(-0.45, 0.55, 0.7);

const land = Uint8Array.from(LAND, (ch) => Number.parseInt(ch, 16));
const experts = Uint8Array.from(EXPERTS, (ch) => Number.parseInt(ch, 16));

const sinT = Math.sin((TILT * Math.PI) / 180);
const cosT = Math.cos((TILT * Math.PI) / 180);

export interface GlobePalette {
  ocean: string;
  /** Unlit to lit land; cells use roughly the first two thirds. */
  land: string[];
  /** Expert twinkles, dim to bright. */
  experts: string[];
}

// Land and accent ramps, both ending on `--color-marketing-blue`; twinkles run
// on into `--color-marketing-blue-dark` so experts still read against the land.
const BLUE_PALETTE: GlobePalette = {
  ocean: "#c4ceff",
  land: ramp([196, 204, 255], [10, 6, 247], 16),
  experts: ramp([77, 111, 255], [0, 6, 183], 16),
};

export interface GlobeFrame {
  /** Edge of the square the disc is drawn in, in CSS pixels. */
  size: number;
  cellW: number;
  cellH: number;
  /** Longitude facing the viewer, in degrees. */
  lon0: number;
  /** Seconds since the globe started; drives the twinkle pulse. */
  elapsed: number;
  /** 0-1 progress of the opening reveal; 1 draws the whole disc. */
  reveal: number;
  palette?: GlobePalette;
}

export function ramp(
  from: [number, number, number],
  to: [number, number, number],
  steps: number,
): string[] {
  return Array.from({ length: steps }, (_, i) => {
    const t = i / (steps - 1);
    const mix = (a: number, b: number) => Math.round(a + (b - a) * t);
    return `rgb(${mix(from[0], to[0])}, ${mix(from[1], to[1])}, ${mix(from[2], to[2])})`;
  });
}

/**
 * Shades every grid cell inside the disc and hands it to `paint` with its
 * `GLYPHS` index, colour and centre. The canvas globe and the About OG image
 * both draw through this, so the two cannot drift apart.
 */
export function shadeGlobe(
  frame: GlobeFrame,
  paint: (glyph: number, color: string, px: number, py: number) => void,
): void {
  const { size, cellW, cellH, lon0, elapsed } = frame;
  const palette = frame.palette ?? BLUE_PALETTE;
  const cols = Math.floor(size / cellW);
  const rows = Math.floor(size / cellH);
  const originX = (size - cols * cellW) / 2;
  const originY = (size - rows * cellH) / 2;

  const radius = size * 0.47;
  const cx = size / 2;
  const cy = size / 2;
  const revealRadius = easeOutCubic(clamp01(frame.reveal)) * 1.05;

  for (let r = 0; r < rows; r++) {
    const py = originY + (r + 0.5) * cellH;
    const y = -(py - cy) / radius;
    for (let c = 0; c < cols; c++) {
      const px = originX + (c + 0.5) * cellW;
      const x = (px - cx) / radius;
      const rr = x * x + y * y;
      if (rr >= 1 || Math.sqrt(rr) > revealRadius) {
        continue;
      }

      // Inverse orthographic projection about (lon0, TILT).
      const z = Math.sqrt(1 - rr);
      const lat = Math.asin(z * sinT + y * cosT) * (180 / Math.PI);
      const lon = lon0 + Math.atan2(x, z * cosT - y * sinT) * (180 / Math.PI);
      const gc =
        ((Math.floor((lon + 180) / 2) % GRID_COLS) + GRID_COLS) % GRID_COLS;
      const gr = Math.min(GRID_ROWS - 1, Math.floor((90 - lat) / 2));
      const gi = gr * GRID_COLS + gc;

      const light = clamp01(
        0.5 + 0.5 * (x * LIGHT[0] + y * LIGHT[1] + z * LIGHT[2]),
      );

      if ((land[gi] ?? 0) / 15 < 0.4) {
        paint(glyphFor(0), palette.ocean, px, py);
        continue;
      }

      const level = experts[gi] ?? 0;
      const texture = 0.6 + 0.4 * hash(gi, 0x9e3779b9);
      const twinkles = level > 0 && hash(gi, 0x632be5ab) < (level / 15) * 0.2;
      if (twinkles) {
        const pulse =
          0.5 + 0.5 * Math.sin(elapsed * 1.7 + hash(gi, 0x1b873593) * 6.283);
        const v = clamp01(0.3 + 0.7 * pulse * light);
        paint(
          glyphFor(0.5 + 0.5 * v),
          shade(palette.experts, v, palette.ocean),
          px,
          py,
        );
      } else {
        // Density stays high so coastlines read as solid; tone comes from
        // colour, which is where the lighting lands.
        const v = clamp01(texture * light);
        paint(
          glyphFor(0.45 + 0.55 * v),
          shade(palette.land, 0.1 + 0.55 * v, palette.ocean),
          px,
          py,
        );
      }
    }
  }
}

function normalize(x: number, y: number, z: number): [number, number, number] {
  const l = Math.hypot(x, y, z);
  return [x / l, y / l, z / l];
}

/** `GLYPHS` index for a 0-1 brightness. */
function glyphFor(v: number): number {
  return Math.min(GLYPHS.length - 1, Math.floor(v * GLYPHS.length));
}

/** Ramp entry for a 0-1 position, clamped into range. */
function shade(rampColors: string[], v: number, fallback: string): string {
  const i = Math.round(clamp01(v) * (rampColors.length - 1));
  return rampColors[i] ?? fallback;
}

/** Stable 0-1 value per grid cell, so texture and twinkles ride the sphere. */
function hash(i: number, salt: number): number {
  let h = Math.imul(i ^ salt, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 0xffffffff;
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
